import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { box, byRow, dots, geometry, outlined, round, taper } from '../effect-guns.sprite';

/**
 * Boogie Blaster (the Disco set's gun, ADR-0160 amended): a boombox blaster. A black boombox in chrome trim, two speaker
 * cones on its side thumping to a beat, a row of equaliser lights dancing on the stock, a chrome handle over it with a
 * little mirror ball hanging from it, turning (its facets' light running round it a frame at a time), a chrome barrel
 * striped in pink neon to a gold muzzle. On a shot (`fire`) the cones punch out white, the equaliser jumps to the top,
 * then all of it settles.
 */

const CASE = ['k', '1', '1', '2'] as const;
const CHROME = ['3', '4', '5', 'w'] as const;
/** The equaliser's colours, bottom to top. */
const EQ: readonly PaletteKey[] = ['l', 'l', 'y', 'o', 'i'];
/** Each bar's height (of 5) a frame, and on a shot. */
const LEVELS = [
  [2, 4, 3, 5, 2, 3],
  [4, 2, 5, 3, 4, 2],
  [3, 5, 2, 4, 3, 5],
  [5, 3, 4, 2, 5, 4],
] as const;

/** A speaker cone at (cx, cy): a chrome rim, the cone in rings, a dust cap; `punch` 0 at rest … 2 pushed out pink. */
function speaker(c: PixelCanvas, cx: number, cy: number, r: number, punch: number): void {
  for (let y = Math.floor(cy - r); y <= cy + r; y++)
    for (let x = Math.floor(cx - r); x <= cx + r; x++) {
      const d = Math.hypot(x + 0.5 - cx, y + 0.5 - cy);
      if (d > r) continue;
      const lit = x + 0.5 - cx + (y + 0.5 - cy) < 0;
      if (d > r - 1.1) c.set(x, y, lit ? 'w' : '4');
      else if (d > r - 2) c.set(x, y, lit ? '2' : '3');
      else if (d < 1.5) c.set(x, y, punch === 2 ? 'w' : lit ? '5' : '4');
      else if (d < 2.3) c.set(x, y, punch === 2 ? 'i' : punch ? '3' : '2');
      else c.set(x, y, punch === 2 ? (lit ? 'i' : 'p') : lit ? '1' : 'k');
    }
}

/** The little mirror ball at (cx, cy), radius r: a lit sphere in squares of 2 px, its glint `turn` squares round. */
function mirrorBall(c: PixelCanvas, cx: number, cy: number, r: number, turn: number): void {
  const tones = ['2', '3', '4', '5', 'w'] as const;
  for (let y = Math.floor(cy - r); y <= cy + r; y++)
    for (let x = Math.floor(cx - r); x <= cx + r; x++) {
      const [dx, dy] = [x + 0.5 - cx, y + 0.5 - cy];
      if (Math.hypot(dx, dy) > r) continue;
      const l = 2.4 - (dx + dy) / r;
      const grout = (x + turn) % 2 === 0 || y % 2 === 0;
      c.set(x, y, tones[Math.max(0, Math.min(4, Math.round(l) - (grout ? 1 : 0)))] as PaletteKey);
    }
  // Its glint, running round it.
  const gx = Math.round(cx - r + 1 + ((turn * 2) % Math.ceil(r * 2 - 1)));
  c.set(gx, Math.round(cy - 1), 'w').set(gx, Math.round(cy - 2), 'y');
}

const boogie = (frame: number, heat = 0) =>
  outlined((c: PixelCanvas) => {
    // The stock: purple, its equaliser lights dancing.
    box(c, 5, 16, 22, 25, [2, 1], byRow(16, 25, ['P', 'P', 'p', 'i'], 1, 2), 'l');
    box(c, 5, 17, 7, 24, [1], (_x, y) => (y < 19 ? 'y' : y > 22 ? 'U' : 'Y'), 'l');
    const level = LEVELS[frame % 4] as readonly number[];
    for (let i = 0; i < 6; i++) {
      const x = 9 + i * 2;
      const h = heat === 3 ? 5 : heat === 2 ? Math.min(5, (level[i] ?? 0) + 2) : (level[i] ?? 0);
      for (let k = 0; k < 5; k++) c.set(x, 23 - k, k < h ? (EQ[k] as PaletteKey) : 'k');
    }
    // The handle: chrome on two posts, the mirror ball hanging from it.
    c.vline(25, 3, 12, '4').vline(42, 3, 12, '3');
    c.hline(25, 42, 2, '5').hline(25, 42, 3, '4');
    c.set(25, 2, 'w');
    c.vline(34, 4, 4, '3');
    mirrorBall(c, 34.5, 8, 3.6, frame);
    // The boombox: black, chrome trim along its top and its foot.
    box(c, 22, 12, 46, 27, [2, 1], byRow(12, 27, CASE, 1, 2), 'r');
    c.hline(23, 45, 13, '5').hline(23, 45, 26, '3');
    // Two speakers thumping: the beat on every other frame.
    const punch = heat >= 2 ? 2 : heat === 1 || frame % 2 === 0 ? 1 : 0;
    speaker(c, 28.5, 20, 5, punch);
    speaker(c, 40, 20, 4.4, heat >= 2 ? 2 : frame % 2 === 1 ? 1 : 0);
    // A neon line between them.
    c.vline(34, 15, 24, heat ? 'w' : 'i').vline(35, 15, 24, 'p');
    // The trigger.
    box(c, 31, 28, 34, 30, [0, 1], (_x, y) => (y === 28 ? 'Y' : 'U'), 'r');
    // The barrel: chrome, ringed in pink neon, a gold muzzle.
    taper(c, 47, 58, 19.5, 3.4, 2.4, round(CHROME));
    for (const x of [49, 53, 57]) c.vline(x, 18, 21, heat || (x + frame * 4) % 12 === 1 ? 'w' : 'i').set(x, 22, 'p');
    taper(c, 59, 61, 19.5, 2.6, 2.2, round(['U', 'Y', 'y', 'w']));
    dots(c, heat ? 'w' : 'i', [
      [61, 19],
      [61, 20],
    ]);
  });

export default sprite({
  name: 'pollinator_disco',
  ...geometry,
  notes: `Boogie Blaster (ADR-0160 amended, the Disco set's gun): a boombox blaster. A black boombox in chrome trim, two
speaker cones on its side thumping in turn (a ring pushed out a frame, chrome rims lit top-left), a pink neon line between
them, a purple stock with six equaliser bars dancing (lime, gold, orange, pink up), a chrome handle on two posts with a
little mirror ball hanging from it (its facets' light running round it over 4 frames), a chrome barrel ringed in pink
neon to a gold muzzle. Fire: the cones punch out pink and white, the equaliser jumps to the top, settles over 3 frames.
Its flair: glints on the mirror ball, its gold glow and the muzzle's pink beating. No team keys. Rubric (4×): S4 R5 P4 L4
C4 A4 F4. Weakest: the mirror ball is 7 px across, a sparkle more than a ball at 1:1 (its flair's glints carry it).`,
  anims: {
    idle: { fps: 6, frames: [boogie(0), boogie(1), boogie(2), boogie(3)] },
    fire: { fps: 20, loop: false, frames: [boogie(0, 3), boogie(1, 2), boogie(2, 1), boogie(3)] },
  },
});
